import type { PostProcessorModule } from "i18next";

// Canonical terms a therapist can choose to refer to the people they treat.
// "student" is not one of them: it is not chosen, it comes from working at a
// training centre, where the people in the room are students and calling them
// patients would be wrong rather than merely unfamiliar.
export type PatientTerm = "patient" | "client" | "consultee" | "student";

// What the professional is called. Same mechanism, one word: a training centre
// has trainers, not therapists.
export type ProfessionalTerm = "therapist" | "trainer";

// The word actually written in the locale files (the default we swap FROM).
const BASE: Record<string, { sing: string; plur: string }> = {
    en: { sing: "patient", plur: "patients" },
    es: { sing: "paciente", plur: "pacientes" },
};

// Per-language singular/plural for each selectable term.
const TERMS: Record<PatientTerm, Record<string, { sing: string; plur: string }>> = {
    patient: {
        en: { sing: "patient", plur: "patients" },
        es: { sing: "paciente", plur: "pacientes" },
    },
    client: {
        en: { sing: "client", plur: "clients" },
        es: { sing: "cliente", plur: "clientes" },
    },
    consultee: {
        en: { sing: "consultee", plur: "consultees" },
        es: { sing: "consultante", plur: "consultantes" },
    },
    student: {
        en: { sing: "student", plur: "students" },
        es: { sing: "alumno", plur: "alumnos" },
    },
};

const PROFESSIONAL_BASE: Record<string, { sing: string; plur: string }> = {
    en: { sing: "therapist", plur: "therapists" },
    es: { sing: "terapeuta", plur: "terapeutas" },
};

// What a meeting with them is called. A school does not hold therapy
// sessions; it holds mentoring. Driven by the professional term rather than
// set on its own, because the two always move together: wherever the person
// is a mentor, what they hold is a mentoría.
const SESSION_BASE: Record<string, { sing: string; plur: string }> = {
    en: { sing: "session", plur: "sessions" },
    es: { sing: "sesión", plur: "sesiones" },
};

const SESSION_TERMS: Record<ProfessionalTerm, Record<string, { sing: string; plur: string }>> = {
    therapist: {
        en: { sing: "session", plur: "sessions" },
        es: { sing: "sesión", plur: "sesiones" },
    },
    trainer: {
        en: { sing: "mentoring session", plur: "mentoring sessions" },
        es: { sing: "mentoría", plur: "mentorías" },
    },
};

const PROFESSIONAL_TERMS: Record<ProfessionalTerm, Record<string, { sing: string; plur: string }>> = {
    therapist: {
        en: { sing: "therapist", plur: "therapists" },
        es: { sing: "terapeuta", plur: "terapeutas" },
    },
    // `trainer` is the value on the wire; "mentor" is the word everybody
    // reads, the school's staff and its students alike. One word for one role:
    // two made the same person sound like two.
    trainer: {
        en: { sing: "mentor", plur: "mentors" },
        es: { sing: "mentor", plur: "mentores" },
    },
};

// The therapist's own choice (set from the therapist context) and, apart from
// it, the one a training centre imposes. Two slots rather than one, because
// the two are set by two effects in two components that run in whatever order
// React runs them: with one shared slot, refreshing the therapist put
// "patient" back over the centre's "student" and a school saw "Pacientes".
// The centre's, when there is one, always wins.
let therapistTerm: PatientTerm = "patient";
let centerTerm: PatientTerm | null = null;

let currentProfessionalTerm: ProfessionalTerm = "therapist";

export function setPatientTerm(term: string | null | undefined): void {
    if (
        term === "client" ||
        term === "consultee" ||
        term === "patient" ||
        term === "student"
    ) {
        therapistTerm = term;
    } else {
        therapistTerm = "patient";
    }
}

/** The term a centre imposes — "student" in a training centre — or null for none. */
export function setCenterPatientTerm(term: PatientTerm | null): void {
    centerTerm = term;
}

export function setProfessionalTerm(term: string | null | undefined): void {
    // Anything unknown falls back to "therapist" rather than rendering a word
    // nobody wrote: a stray value must not empty the interface of its nouns.
    // "mentor" is accepted as well as "trainer": it is the word the interface
    // shows, so a caller reaching for it is asking for the right thing.
    currentProfessionalTerm =
        term === "trainer" || term === "mentor" ? "trainer" : "therapist";
}

export function getProfessionalTerm(): ProfessionalTerm {
    return currentProfessionalTerm;
}

export function getPatientTerm(): PatientTerm {
    return centerTerm ?? therapistTerm;
}

function matchCase(source: string, target: string): string {
    if (source === source.toUpperCase()) return target.toUpperCase();
    if (source[0] === source[0].toUpperCase()) {
        return target[0].toUpperCase() + target.slice(1);
    }
    return target;
}

function swap(text: string, from: string, to: string): string {
    if (from === to) return text;
    // Whole-word, case-insensitive; preserve the matched word's casing.
    const re = new RegExp(`\\b${from}\\b`, "gi");
    return text.replace(re, (m) => matchCase(m, to));
}

/**
 * The signed-in account's own session — signing in, signing out, a session
 * that expired — is not a therapy session, and must not become a mentoring.
 * Those phrases are set aside before the session swap and put back after it.
 */
const ACCOUNT_SESSION: Record<string, RegExp> = {
    es: /\b(?:(?:cerrar|cierra|cierre|cerró|iniciar|inicia|inicie|inició|inicio|vuelve a iniciar)(?:\s+de)?\s+sesi[oó]n|sesi[oó]n\s+(?:iniciada|cerrada|caducada|expirada|ha\s+caducado|ha\s+expirado))\b/gi,
    en: /\b(?:session\s+(?:has\s+)?expired|session\s+timed?\s*out)\b/gi,
};

function swapSessions(text: string, lng: string, from: { sing: string; plur: string }, to: { sing: string; plur: string }): string {
    // A private-use character marks each phrase set aside: it never occurs in
    // a translation, and (unlike a control character) a regex may name it.
    const kept: string[] = [];
    const pattern = ACCOUNT_SESSION[lng];
    const masked = pattern
        ? text.replace(pattern, (match) => {
              kept.push(match);
              return `\uE000${kept.length - 1}\uE000`;
          })
        : text;
    const swapped = swap(swap(masked, from.plur, to.plur), from.sing, to.sing);
    return swapped.replace(/\uE000(\d+)\uE000/g, (_, index) => kept[Number(index)]);
}

/**
 * i18next post-processor that rewrites the canonical "patient"/"paciente"
 * (singular + plural) in every translated string to the therapist's chosen
 * term. No-op when the term is the default. Language comes from the resolved
 * i18n language so it works in EN and ES.
 */
export const patientTermPostProcessor: PostProcessorModule = {
    type: "postProcessor",
    name: "patientTerm",
    process(value: string, key, _options, translator): string {
        if (typeof value !== "string") return value;
        const currentTerm = getPatientTerm();
        if (currentTerm === "patient" && currentProfessionalTerm === "therapist") return value;
        // Never rewrite the terminology-picker's own labels/options — they must
        // always show the real term names.
        const k = Array.isArray(key) ? key[0] : key;
        if (typeof k === "string" && k.startsWith("profile.patientTerm")) return value;
        const lng = (translator?.language || "en").split("-")[0];
        let out = value;

        const base = BASE[lng];
        const target = TERMS[currentTerm][lng];
        if (base && target) {
            // Plural first so the singular rule can't touch an already-swapped word.
            out = swap(out, base.plur, target.plur);
            out = swap(out, base.sing, target.sing);
        }

        const professionalBase = PROFESSIONAL_BASE[lng];
        const professionalTarget = PROFESSIONAL_TERMS[currentProfessionalTerm][lng];
        if (professionalBase && professionalTarget) {
            out = swap(out, professionalBase.plur, professionalTarget.plur);
            out = swap(out, professionalBase.sing, professionalTarget.sing);
        }

        const sessionBase = SESSION_BASE[lng];
        const sessionTarget = SESSION_TERMS[currentProfessionalTerm][lng];
        if (sessionBase && sessionTarget) {
            out = swapSessions(out, lng, sessionBase, sessionTarget);
        }

        return out;
    },
};
